import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatGYD } from "@/lib/api";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import MediaLightbox from "@/components/MediaLightbox";
import SteadyHandBadge from "@/components/SteadyHandBadge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Star,
  Trophy,
  CarProfile,
  ShieldCheck,
  ArrowLeft,
  ChatCircleDots,
  Lightning,
  WhatsappLogo,
  Phone,
  Lock,
} from "@phosphor-icons/react";

function Stars({ value = 0, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} weight={value >= n ? "fill" : "regular"} className={value >= n ? "text-primary" : "text-foreground/30"} />
      ))}
    </div>
  );
}

export default function PublicDriverProfile() {
  const { driverId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [replyOpen, setReplyOpen] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/d/${driverId}`);
      setData(data);
    } catch {
      toast.error("Driver not found");
    }
  };

  useEffect(() => { load(); }, [driverId]);

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">Loading…</div>
      </div>
    );
  }

  const isOwner = user?.user_id === data.driver_id;
  const canComment = user && user.role && !isOwner && !data.viewer_has_commented;

  const submitComment = async () => {
    if (!rating || !text.trim()) {
      toast.error("Pick a rating and write a comment");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/d/${driverId}/comments`, { rating, text: text.trim() });
      toast.success("Review posted");
      setText(""); setRating(0);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const submitReply = async (commentId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/drivers/comments/${commentId}/reply`, { reply: replyText.trim() });
      setReplyOpen(null); setReplyText("");
      toast.success("Reply posted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-2 border-foreground bg-background sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2 font-display text-xl">
            <span className="inline-block w-3 h-3 bg-primary" />
            RideGY
          </Link>
          <Link to="/leaderboard" data-testid="link-leaderboard" className="px-3 py-1.5 text-sm font-medium border-2 border-foreground inline-flex items-center gap-1.5">
            <Trophy size={16} weight="duotone" /> Leaderboard
          </Link>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <Link to="/leaderboard" className="font-mono text-xs uppercase tracking-wider text-foreground/60 inline-flex items-center gap-1 mb-4 hover:text-foreground">
          <ArrowLeft size={14} /> All drivers
        </Link>

        {/* Hero */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-0 border-2 border-foreground">
          <div className="p-8">
            <div className="flex items-start gap-4">
              {data.picture ? (
                <img src={data.picture} alt="" className="w-16 h-16 object-cover border-2 border-foreground" />
              ) : (
                <div className="w-16 h-16 bg-secondary border-2 border-foreground flex items-center justify-center font-display text-2xl">
                  {(data.name || "D")[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-3xl tracking-tighter truncate" data-testid="profile-name">{data.name}</h1>
                  {data.verified && (
                    <span title="Verified" className="inline-flex items-center justify-center w-5 h-5 bg-accent text-white font-mono text-[10px]">✓</span>
                  )}
                  <SteadyHandBadge steady={data.safety?.steady_hand} size="md" />
                  {data.online && (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-accent inline-flex items-center gap-1">
                      <Lightning size={12} weight="fill" /> Online
                    </span>
                  )}
                </div>
                <div className="text-foreground/70 flex items-center gap-2 mt-1">
                  <CarProfile size={16} /> {data.vehicle_color} {data.vehicle_make} {data.vehicle_model} · <span className="font-mono">{data.vehicle_plate}</span>
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <Stars value={Math.round(data.rating_avg || 0)} />
                  <span className="font-mono text-xs">{data.rating_avg ? data.rating_avg.toFixed(1) : "—"} · {data.rating_count} trip ratings</span>
                </div>
              </div>
            </div>
            {data.bio && (
              <p className="mt-6 text-foreground/80 max-w-2xl leading-relaxed">{data.bio}</p>
            )}

            {data.phone_locked && !user && (
              <div className="mt-6 border-2 border-foreground bg-background p-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-[4px_4px_0_0_#FFB800]" data-testid="profile-contact-locked">
                <div className="flex items-center gap-2 text-sm text-foreground/80">
                  <Lock size={18} weight="fill" className="text-primary shrink-0" />
                  <span>
                    Phone hidden to protect our drivers.{" "}
                    <strong>Sign in to message or call {(data.name || "this driver").split(" ")[0]}.</strong>
                  </span>
                </div>
                <button
                  data-testid="profile-contact-signin-btn"
                  onClick={() => {
                    try { sessionStorage.setItem("ridegy:post_signin_redirect", `/d/${data.driver_id}`); } catch { /* private mode */ }
                    toast.message("Sign in to contact drivers", {
                      description: "Continue with Google — we'll bring you right back to this driver.",
                    });
                    setTimeout(() => loginWithGoogle(), 350);
                  }}
                  className="ml-auto inline-flex items-center justify-center gap-2 h-10 px-4 bg-foreground text-background font-semibold border-2 border-foreground hover:bg-foreground/90 shrink-0"
                >
                  Sign in to contact
                </button>
              </div>
            )}

            {data.phone && (() => {
              const digits = data.phone.replace(/\D/g, "");
              const firstName = (data.name || "").split(" ")[0];
              const myFirst = user ? (user.name || "").split(" ")[0] : "";
              const profileUrl = user ? `${window.location.origin}/r/${user.user_id}` : "";
              const lines = [
                `Hi ${firstName}, I found you on RideGY and would like to book a ride.`,
                myFirst ? `I'm ${myFirst}. I would like the discount.` : "I would like the discount.",
                profileUrl ? `My profile: ${profileUrl}` : "",
              ].filter(Boolean);
              const waLink = `https://wa.me/${digits}?text=${encodeURIComponent(lines.join("\n"))}`;
              const telLink = `tel:+${digits}`;
              const logContact = (source) => {
                api.post(`/d/${data.driver_id}/contact`, { source }).catch(() => {});
              };
              return (
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    data-testid="profile-whatsapp-btn"
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => logContact("whatsapp")}
                    className="inline-flex items-center gap-2 h-12 px-5 bg-accent text-white font-semibold border-2 border-foreground hover:bg-accent/90"
                  >
                    <WhatsappLogo size={20} weight="fill" /> Message on WhatsApp
                    {data.whatsapp_clicks > 0 && (
                      <span className="ml-1 font-mono text-[10px] bg-background text-foreground px-1.5 py-0.5">
                        {data.whatsapp_clicks}
                      </span>
                    )}
                  </a>
                  <a
                    data-testid="profile-call-btn"
                    href={telLink}
                    onClick={() => logContact("call")}
                    className="inline-flex items-center gap-2 h-12 px-5 bg-background text-foreground font-semibold border-2 border-foreground hover:bg-secondary"
                  >
                    <Phone size={18} weight="fill" /> Call {data.phone}
                    {data.call_clicks > 0 && (
                      <span className="ml-1 font-mono text-[10px] bg-foreground text-background px-1.5 py-0.5">
                        {data.call_clicks}
                      </span>
                    )}
                  </a>
                </div>
              );
            })()}
          </div>
          <div className="border-l-2 border-foreground p-6 bg-primary">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground/60">Driver score</div>
            <div className="font-display text-6xl leading-none mt-2" data-testid="profile-points">{data.points}</div>
            <div className="font-mono text-xs uppercase tracking-wider text-foreground/70 mt-2">points</div>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <div className="border-2 border-foreground p-2 bg-background">
                <div className="font-mono text-[10px] uppercase text-foreground/60">Trips</div>
                <div className="font-display text-lg">{data.total_trips}</div>
              </div>
              <div className="border-2 border-foreground p-2 bg-background">
                <div className="font-mono text-[10px] uppercase text-foreground/60">Reviews</div>
                <div className="font-display text-lg">{data.comments_count}</div>
              </div>
            </div>
            {data.safety && (
              <div
                data-testid="profile-safety-score"
                className={`mt-3 border-2 border-foreground p-3 ${
                  data.safety.compliance_rate >= 80
                    ? "bg-accent text-background"
                    : data.safety.compliance_rate >= 50
                    ? "bg-background"
                    : "bg-destructive text-destructive-foreground"
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider opacity-80">
                    Safety score
                  </div>
                  <div
                    data-testid="profile-safety-rate"
                    className="font-display text-2xl leading-none"
                  >
                    {data.safety.compliance_rate}%
                  </div>
                </div>
                <div className="mt-1 font-mono text-[10px] leading-snug opacity-70">
                  slowed down before 120 km/h · {data.safety.resolved_events} events
                </div>
                {data.safety.steady_hand && (
                  <div className="mt-2">
                    <SteadyHandBadge steady size="pill" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Media gallery */}
        <section className="mt-10">
          <h2 className="font-display text-2xl tracking-tighter mb-4">Gallery</h2>
          {data.media.length === 0 ? (
            <div className="border-2 border-dashed border-foreground/30 p-8 text-center text-foreground/60">
              No photos or videos yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {data.media.map((m, idx) => (
                <button
                  key={m.media_id}
                  type="button"
                  onClick={() => setGalleryIndex(idx)}
                  className="aspect-square border-2 border-foreground overflow-hidden bg-secondary relative text-left cursor-pointer"
                  data-testid={`profile-media-${m.media_id}`}
                >
                  {m.kind === "image" ? (
                    <img src={`${API}/public/media/${m.storage_path}`} alt={m.caption} className="w-full h-full object-cover" />
                  ) : (
                    <video
                      src={`${API}/public/media/${m.storage_path}`}
                      poster={m.poster_path ? `${API}/public/media/${m.poster_path}` : undefined}
                      className="w-full h-full object-cover pointer-events-none"
                      preload="metadata"
                      muted
                      playsInline
                    />
                  )}
                  {m.caption && (
                    <div className="absolute bottom-0 inset-x-0 bg-foreground/80 text-background text-xs p-2 truncate">{m.caption}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          <MediaLightbox
            items={data.media || []}
            index={galleryIndex}
            mediaUrl={(p) => `${API}/public/media/${p}`}
            onClose={() => setGalleryIndex(null)}
            onIndexChange={setGalleryIndex}
          />
        </section>

        {/* Comments */}
        <section className="mt-12">
          <h2 className="font-display text-2xl tracking-tighter mb-4 flex items-center gap-2">
            <ChatCircleDots size={24} weight="duotone" /> Reviews ({data.comments_count})
          </h2>

          {canComment && (
            <div className="border-2 border-foreground p-4 bg-card mb-6">
              <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mb-2">Leave a review</div>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    data-testid={`comment-rate-${n}`}
                    onClick={() => setRating(n)}
                    className={`p-1 border-2 ${rating >= n ? "bg-primary border-foreground" : "border-foreground/20"}`}
                  >
                    <Star size={18} weight={rating >= n ? "fill" : "regular"} />
                  </button>
                ))}
              </div>
              <Textarea
                data-testid="comment-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Tell other riders about your experience…"
                className="rounded-none border-2 border-foreground/30"
                maxLength={600}
              />
              <Button
                data-testid="comment-submit-btn"
                onClick={submitComment}
                disabled={busy || !rating || !text.trim()}
                className="mt-3 rounded-none bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
              >
                Post review
              </Button>
            </div>
          )}

          {user && data.viewer_has_commented && (
            <div className="border-2 border-accent p-3 text-sm text-accent mb-6 font-mono uppercase tracking-wider text-[10px]">
              You've already reviewed this driver.
            </div>
          )}

          {data.comments.length === 0 ? (
            <div className="border-2 border-dashed border-foreground/30 p-8 text-center text-foreground/60">
              No reviews yet. Be the first to leave one.
            </div>
          ) : (
            <ul className="space-y-3">
              {data.comments.map((c) => (
                <li key={c.comment_id} className="border-2 border-foreground p-4 bg-card" data-testid={`comment-${c.comment_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {c.rider_picture ? (
                        <img src={c.rider_picture} alt="" className="w-7 h-7 object-cover border border-foreground" />
                      ) : (
                        <div className="w-7 h-7 bg-secondary border border-foreground flex items-center justify-center text-xs font-display">
                          {(c.rider_name || "R")[0]}
                        </div>
                      )}
                      <span className="font-medium text-sm">{c.rider_name}</span>
                      <Stars value={c.rating} size={12} />
                    </div>
                    <span className="font-mono text-[10px] text-foreground/50">{(c.created_at || "").slice(0, 10)}</span>
                  </div>
                  <p className="mt-2 text-sm text-foreground/80">{c.text}</p>

                  {c.reply && (
                    <div className="mt-3 ml-6 border-l-2 border-primary pl-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                        {data.name} replied · {(c.reply_at || "").slice(0, 10)}
                      </div>
                      <p className="text-sm mt-1">{c.reply}</p>
                    </div>
                  )}

                  {isOwner && !c.reply && (
                    <div className="mt-3">
                      {replyOpen === c.comment_id ? (
                        <div>
                          <Textarea
                            data-testid={`reply-text-${c.comment_id}`}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a reply…"
                            className="rounded-none border-2 border-foreground/30"
                            maxLength={600}
                          />
                          <div className="flex gap-2 mt-2">
                            <Button
                              data-testid={`reply-submit-${c.comment_id}`}
                              size="sm"
                              onClick={() => submitReply(c.comment_id)}
                              className="rounded-none bg-foreground text-background"
                            >
                              Send reply
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setReplyOpen(null); setReplyText(""); }} className="rounded-none border-2 border-foreground">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          data-testid={`reply-open-${c.comment_id}`}
                          onClick={() => { setReplyOpen(c.comment_id); setReplyText(""); }}
                          className="font-mono text-[10px] uppercase tracking-wider underline"
                        >
                          Reply
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
